import { Component, Input, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { User } from '../../interfaces/user.interface';
import { UserService } from '../../services/user/user.service';
import {
  EMPTY,
  Observable,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { RoleService } from '../../services/role/role.service';
import { GlobalRole } from '@app/shared/interfaces';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProjectService } from '../../services/project/project.service';
import { ToastrService } from 'ngx-toastr';
import { ProjectInviteResponse } from '../../interfaces/project-invite.interface';
import { Project } from '../../interfaces/project.interface';
import { GeneralHelpers } from '../../helpers/general.helper';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';
import { CONTRIBUTOR_ROLE_ID, RELOAD_PROJECT_TEAM } from '../../constants/general.constants';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MessageService } from '../../services/message/message.service';

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-manage-team',
  templateUrl: './manage-team.component.html',
  styleUrls: ['./manage-team.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    // Material
    MatChipsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatListModule,
    MatDividerModule,
    MatTooltipModule,
    // Form support
    FormsModule,
    ReactiveFormsModule,

    // Layout
    FlexLayoutModule,
  ],
})
export class ManageTeamComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private _formBuilder = inject(FormBuilder);
  private projectService = inject(ProjectService);
  private toastr = inject(ToastrService);
  private messageService = inject(MessageService);

  @Input() projectId!: any;

  userCtrl = new FormControl();

  public usersForm!: any;

  project = signal<Project | any>({});

  users: User[] | any = [];

  projectUsers = signal<User[] | any>([]);

  projectInvites = signal<ProjectInviteResponse[] | any>([]);

  roles = signal<GlobalRole[] | any>([]);

  separatorKeysCodes = [COMMA, ENTER];

  // ────────────────────────────────────────────────────────────────────────────────
  // User Autocomplete
  usersAutocompleteData!: Observable<unknown> | undefined | any;

  userLoading = false;
  noUsersFound = signal(false);

  ngOnInit() {
    this.userCtrl.setValidators(Validators.required);

    this.usersForm = this._formBuilder.group({
      user: this.userCtrl,
      role: ['', [Validators.required]],
      // role: ['', [this.projectVersionToRoleValidator.bind(this)]],
    });

    this.getProject(this.projectId);
    this.getRoles();
    this.prepareUserAutocomplete();

    if (this.roles().length > 0) {
      this.usersForm.get('role')?.setValue(this.roles()[0]);
    }
  }

  // eslint-disable-next-line
  ngOnDestroy() { }

  // ────────────────────────────────────────────────────────────────────────────────
  // User autocomplete
  // ────────────────────────────────────────────────────────────────────────────────

  private prepareUserAutocomplete() {
    this.usersAutocompleteData = this.userCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((val: string): any => {
        return this.getUsers(val);
      })
    );
  }

  private getUsers(val: string): Observable<any[]> {
    if (typeof val !== 'string') {
      return EMPTY;
    }
    val = GeneralHelpers.removeSpaces(val);
    this.noUsersFound.set(false);
    return this.userService.getUsers().pipe(
      map((response) =>
        response.filter((option: any) => {
          if (val === '') {
            return true; // Show all users when input is empty
          }

          const searchVal = val.toLowerCase();

          // Search by name
          if (option.name && option.name.toLowerCase().includes(searchVal)) {
            return true;
          }

          // Search by email
          if (option.email && option.email.toLowerCase().includes(searchVal)) {
            return true;
          }

          return false;
        })
      ),
      tap((users: User[]) => {
        if (users.length === 0) {
          this.noUsersFound.set(true);
        }
      })
    );
  }

  public displayUser(user: User | string | any) {
    if (user === null) {
      return '';
    }
    if (typeof user === 'string') {
      return user;
    }
    if (typeof user === 'object') {
      return `${user.email} | ${user.name}`;
    }
    return '';
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Api calls
  // ────────────────────────────────────────────────────────────────────────────────

  private getInvitesForProject(): void {
    this.projectService
      .getInvitesForProject(this.projectId)
      .subscribe((invites: ProjectInviteResponse[]) => {
        this.projectInvites.set(this.processInvites(invites));
      });
  }

  private getRoles(): void {
    this.roleService.get().subscribe((roles: any) => {
      this.roles.set(roles);
      this.getInvitesForProject();
    });
  }

  private getProject(projectId: number): void {
    this.projectService.getById(`${projectId}`).subscribe((project: any) => {
      this.project.set(project);
      this.projectUsers.set(project.users);
    });
  }

  public sendUserInvite(): void {
    if (this.usersForm.invalid) {
      return;
    }

    const userValue = this.usersForm.get('user')?.value;
    const role = this.usersForm.get('role')?.value as unknown as GlobalRole;

    // Check if user is a string (email) instead of a User object
    if (typeof userValue === 'string') {
      const email = userValue.trim();
      // Validate email format with a more comprehensive regex

      if (GeneralHelpers.isEmail(email)) {
        // First check if the user exists with this email
        this.userService.searchUsersByEmail(email).subscribe({
          next: (users) => {
            if (users && users.length > 0) {
              // User exists, use their userId
              this.sendInviteWithUserData({
                userId: users[0].userId,
                email: users[0].email
              } as User, role);
            } else {
              // User doesn't exist, show a message that direct email invites aren't supported
              this.toastr.error('User with this email not found. Please select a user from the dropdown.');
            }
          },
          error: (error) => {
            console.error('Error searching for user:', error);
            this.toastr.error('Error searching for user. Please try again.');
          }
        });
      } else {
        this.toastr.error('Please enter a valid email address');
      }
    } else {
      // User selected from dropdown
      this.sendInviteWithUserData(userValue as User, role);
    }
  }

  private sendInviteWithUserData(user: User, role: GlobalRole): void {
    const inviteData: { inviteeId?: number; email?: string; roleId: number } = {
      roleId: role.roleId || 0,
    };

    // If user has userId, use it, otherwise use email
    if ('userId' in user && user.userId) {
      inviteData.inviteeId = user.userId;
    } else if ('email' in user && user.email) {
      inviteData.email = user.email;
    } else {
      this.toastr.error('Invalid user information');
      return;
    }

    this.projectService
      .inviteUserToProject(this.projectId, inviteData)
      .subscribe({
        next: () => {
          this.toastr.success('User invited successfully');
          this.usersForm.reset();
          this.getInvitesForProject();
          this.resetFrom();
          this.messageService.sendMessage(RELOAD_PROJECT_TEAM, { projectId: this.projectId });
        },
        error: (error) => {
          console.error('Error inviting user:', error);

          // Handle specific error cases
          if (error.status === 404) {
            this.toastr.error('User not found. Please check the email address.');
          } else if (error.status === 400) {
            if (error.error?.message?.includes('Json deserialize error')) {
              this.toastr.error('The server does not support email invitations. Please select a user from the dropdown.');
            } else if (error.error?.message) {
              this.toastr.error(error.error.message);
            } else {
              this.toastr.error('Invalid request. Please try selecting a user from the dropdown.');
            }
          } else if (error.error?.message) {
            this.toastr.error(error.error.message);
          } else {
            this.toastr.error('Failed to invite user. Please try again.');
          }
        }
      });
  }

  public manageUserInvitation(invite: ProjectInviteResponse): void {
    if (invite.response === 'Accepted') {
      this.removeUserFromProjectTeam(invite);
    } else if (invite.response === 'Rejected') {
      this.cancelInvite(invite);
    } else if (invite.response === 'Open') {
      this.cancelInvite(invite);
    }
  }

  private cancelInvite(invite: ProjectInviteResponse): void {
    if (!invite.projectId || !invite.inviteId) {
      this.toastr.error('Invalid invite data');
      return;
    }

    this.projectService
      .cancelInviteForProject(invite.projectId, invite.inviteId)
      .subscribe(() => {
        this.toastr.success('Invite cancelled successfully');
        this.getInvitesForProject();
      });
  }

  private removeUserFromProjectTeam(invite: ProjectInviteResponse): void {
    if (!invite.projectId || !invite.inviteeId) {
      this.toastr.error('Invalid invite data');
      return;
    }

    this.projectService
      .removeUserFromProjectTeam(invite.projectId, invite.inviteeId)
      .subscribe(() => {
        this.toastr.success('User was removed successfully from team');
        this.getInvitesForProject();
        this.messageService.sendMessage(RELOAD_PROJECT_TEAM, { projectId: this.projectId });
      });
  }

  private resetFrom(): void {
    GeneralHelpers.resetForm(this as any, 'usersForm');
  }

  public getDisplayName(name: string, isHidden: boolean): string {
    return isHidden ? '*'.repeat(name.length) : name;
  }

  public getInviteStatusClass(invite: ProjectInviteResponse): string {
    if (invite.response === 'Accepted') {
      return 'invite-accepted';
    } else if (invite.response === 'Rejected') {
      return 'invite-rejected';
    } else if (invite.response === 'Open') {
      return 'invite-open';
    }
    return '';
  }

  private processInvites(
    invites: ProjectInviteResponse[]
  ): ProjectInviteResponse[] {
    return invites.map((invite: ProjectInviteResponse) => ({
      ...invite,
      roleName:
        this.roles().find((role: GlobalRole) => role.roleId === invite.roleId)
          ?.name || 'Unknown Role',
    }));
  }

  private projectVersionToRoleValidator(formControl: FormControl): any {
    const role = formControl?.value;
    let validation: any = null;

    if (!role) {
      validation = { required: true };
    }

    if (role?.roleId === CONTRIBUTOR_ROLE_ID && this.project()?.versions?.length <= 1) {
      validation = { invalidRole: true };
    }

    return validation;
  }
  // ────────────────────────────────────────────────────────────────────────────────
  // Api calls
  // ────────────────────────────────────────────────────────────────────────────────
}
